import type { ScreeningProviderConfiguration } from "@repo/models";

import { GridFiltersController, useGridFilters } from "@repo/baseCloud";
import { Dialog, DialogContent, DialogHeader, DialogTitle, FilterDropdown, FiltersV2 } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { ConfigurationsFilter } from "../types";

import BackgroundChecksApi from "../../../api/backgroundChecksApi";
import { useFilterItemQuery } from "../../../api/queries";
import * as Queries from "../../../api/queries";
import { ConfigurationForm } from "./ConfigurationForm";
import { ConfigurationsGrid } from "./ConfigurationsGrid";

const initialFilters: ConfigurationsFilter = {
	currentPage: 1,
	itemsPerPage: 10,
	marketerId: undefined,
	screeningProviderId: undefined,
};

export function ConfigurationsPage() {
	const {
		filters,
		setFilters,
		appliedFilters: executedFilters,
		setAppliedFilters: setExecutedFilters,
		filtersProps,
	} = useGridFilters(initialFilters);
	const [editingConfig, setEditingConfig] = useState<Partial<ScreeningProviderConfiguration> | null>(null);

	const providersQuery = useQuery({
		queryKey: ["screeningProviders"],
		queryFn: () => BackgroundChecksApi.getScreeningProviders(),
	});

	const [marketers] = useFilterItemQuery([Queries.MARKETERS], () => GridFiltersController.getMarketers());

	const filterChanged = useCallback(
		(value: any, name: string) => {
			setFilters((prev) => ({ ...prev, [name]: value }));
		},
		[setFilters],
	);

	const handleEdit = (config: ScreeningProviderConfiguration) => {
		setEditingConfig(config);
	};

	const handleNew = () => {
		setEditingConfig({});
	};

	const handleSave = () => {
		setEditingConfig(null);
		setExecutedFilters({ ...executedFilters }); // Trigger refetch
	};

	return (
		<div className="flex h-full flex-col gap-4">
			<FiltersV2 {...filtersProps}>
				<FilterDropdown
					id="screeningProviderId"
					objectName="Providers"
					values={providersQuery.data ?? []}
					valueProp="ScreeningProviderId"
					textProp="Name"
					value={filters.screeningProviderId}
					changeHandler={filterChanged}
					fullLength
					allOption
				/>
				<FilterDropdown
					id="marketerId"
					objectName="Marketers"
					values={marketers.data ?? []}
					valueProp="id"
					textProp="value"
					value={filters.marketerId}
					changeHandler={filterChanged}
					fullLength
					allOption
				/>
			</FiltersV2>

			<ConfigurationsGrid
				filters={executedFilters}
				setFilters={setExecutedFilters}
				onEdit={handleEdit}
				onNew={handleNew}
			/>

			<Dialog open={!!editingConfig} onOpenChange={(open) => !open && setEditingConfig(null)}>
				<DialogContent className="sm:max-w-[800px]">
					<DialogHeader>
						<DialogTitle>
							{editingConfig?.marketerId ? "Edit Configuration" : "New Configuration"}
						</DialogTitle>
					</DialogHeader>
					{editingConfig && (
						<ConfigurationForm
							key={editingConfig.marketerId || "new"}
							configuration={editingConfig}
							onSave={handleSave}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
